import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Select,
  Space,
  Table,
  type TableColumnProps,
} from '@arco-design/web-react';
import { IconRefresh } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';
import { PageHeader } from '@/components/ui';
import { walletLedgerApi } from '@/api/wallet-ledger';
import type {
  WalletLedgerBizType,
  WalletLedgerListQuery,
  WalletLedgerRecord,
} from '@/types/wallet-ledger';
import { formatMoney } from '@/utils/money';

const { RangePicker } = DatePicker;

const bizTypeLabels: Record<WalletLedgerBizType, string> = {
  CARD_OPEN: '开卡扣款',
  CARD_OPEN_FEE: '开卡手续费',
  CARD_OPEN_ROLLBACK: '开卡失败退回',
  CARD_REFILL: '卡充值扣款',
  CARD_REFILL_ROLLBACK: '充值失败退回',
  CARD_WITHDRAW: '卡提现入账',
  CARD_WRITEOFF: '销卡退回',
  ADMIN_CREDIT: '平台划入',
  ADMIN_DEBIT: '平台划出',
  PARENT_CREDIT: '下级回收入账',
  PARENT_DEBIT: '向下级下发',
  CHILD_CREDIT: '上级下发入账',
  CHILD_DEBIT: '被上级回收',
  FEE_PROFIT_DEPOSIT: '入账手续费利差',
  FEE_PROFIT_CARD_OPEN: '开卡手续费利差',
};

const bizTypeOptions: Array<{ label: string; value: WalletLedgerBizType | 'ALL' }> = [
  { label: '全部业务', value: 'ALL' },
  ...(
    Object.entries(bizTypeLabels) as Array<[WalletLedgerBizType, string]>
  ).map(([value, label]) => ({ label, value })),
];

export default function WalletLedgerView() {
  const [query, setQuery] = useState<WalletLedgerListQuery>({
    page: 1,
    pageSize: 20,
  });
  const [list, setList] = useState<WalletLedgerRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await walletLedgerApi.list(query);
      setList(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  const updateQuery = (next: Partial<WalletLedgerListQuery>) =>
    setQuery((prev) => ({ ...prev, ...next, page: 1 }));

  const columns: TableColumnProps<WalletLedgerRecord>[] = [
    {
      title: '记账时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: '客户编号', dataIndex: 'customerNo', width: 120 },
    {
      title: '上次余额',
      dataIndex: 'balanceBeforeCents',
      width: 120,
      align: 'right',
      render: (v: number) => (
        <span style={{ fontFamily: 'SFMono-Regular, Consolas, monospace' }}>{formatMoney(v)}</span>
      ),
    },
    {
      title: '本次发生额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right',
      render: (v: number) => (
        <span
          style={{
            color: v >= 0 ? 'var(--up-color-success)' : 'var(--up-color-text-primary)',
            fontFamily: 'SFMono-Regular, Consolas, monospace',
          }}
        >
          {v >= 0 ? '+' : ''}
          {formatMoney(v)}
        </span>
      ),
    },
    {
      title: '本次余额',
      dataIndex: 'balanceAfterCents',
      width: 120,
      align: 'right',
      render: (v: number) => (
        <span style={{ fontFamily: 'SFMono-Regular, Consolas, monospace', fontWeight: 600 }}>
          {formatMoney(v)}
        </span>
      ),
    },
    {
      title: '扣费业务',
      dataIndex: 'bizType',
      width: 130,
      render: (v: WalletLedgerBizType) => bizTypeLabels[v] ?? v,
    },
    {
      title: '关联单号',
      dataIndex: 'bizRef',
      width: 200,
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#86909C' }}>—</span>,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#86909C' }}>—</span>,
    },
  ];

  return (
    <div className="up-page">
      <PageHeader
        title="资金流水"
        subtitle="账户钱包变动明细：上次余额、本次发生额、本次余额及扣费业务类型，可按时间与业务筛选"
        extra={
          <Button icon={<IconRefresh />} onClick={() => fetchData()}>
            刷新
          </Button>
        }
      />

      <div className="up-section-card" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            onChange={(dateStrings) =>
              updateQuery({
                startDate: dateStrings?.[0] || undefined,
                endDate: dateStrings?.[1] || undefined,
              })
            }
          />
          <Select
            placeholder="扣费业务"
            style={{ width: 160 }}
            value={query.bizType ?? 'ALL'}
            onChange={(v) =>
              updateQuery({ bizType: v === 'ALL' ? undefined : (v as WalletLedgerBizType) })
            }
          >
            {bizTypeOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>

      <div className="up-section-card">
        <Table
          rowKey="id"
          loading={loading}
          data={list}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{
            total,
            current: query.page,
            pageSize: query.pageSize,
            sizeCanChange: true,
            showTotal: true,
            onChange: (page, pageSize) => setQuery((p) => ({ ...p, page, pageSize })),
          }}
        />
      </div>
    </div>
  );
}
